import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Shield, BookOpen, CreditCard, Cookie, Award } from 'lucide-react';

interface LegalLayoutProps {
  children: React.ReactNode;
}

const legalPages = [
  {
    title: "Mentions légales",
    path: "/legal/mentions",
    icon: FileText
  },
  {
    title: "Politique de confidentialité",
    path: "/legal/privacy",
    icon: Shield
  },
  {
    title: "Conditions d'utilisation",
    path: "/legal/terms",
    icon: BookOpen
  },
  {
    title: "Conditions de vente",
    path: "/legal/sales",
    icon: CreditCard
  },
  {
    title: "Politique de cookies",
    path: "/legal/cookies",
    icon: Cookie
  },
  {
    title: "Conformité logicielle",
    path: "/legal/compliance",
    icon: Award
  }
];

export const LegalLayout: React.FC<LegalLayoutProps> = ({ children }) => {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      <div className="container mx-auto p-4 lg:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Pages légales</h1>
          <p className="text-muted-foreground">
            Informations légales et conditions d'utilisation de notre service
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation - Mobile: Horizontal scroll, Desktop: Vertical */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="p-4">
                <nav>
                  <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                    {legalPages.map((page) => {
                      const Icon = page.icon;
                      const isActive = location.pathname === page.path;
                      
                      return (
                        <NavLink
                          key={page.path}
                          to={page.path}
                          className={`
                            flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium
                            transition-all duration-200 whitespace-nowrap lg:whitespace-normal
                            ${isActive 
                              ? 'bg-primary text-primary-foreground shadow-sm' 
                              : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                            }
                          `}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="hidden sm:inline">{page.title}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="min-h-[600px]">
              <ScrollArea className="h-[80vh]">
                <CardContent className="p-6 lg:p-8">
                  {children}
                </CardContent>
              </ScrollArea>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};